import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react'
import {
    DEPARTMENTS,
    calculatePerformance,
    getStatusColorFromText,
    type DepartmentKpi,
    type KpiItem,
} from '../../../../app/modules/Dashboard/components/kpiDepartmentsData'

const AUTO_SLIDE_MS = 3000

const TrendIcon: FC<{ tranding: string }> = ({ tranding }) => {
    if (tranding === 'up') {
        return <TrendingUp className="text-success" size={16} />
    }
    if (tranding === 'down') {
        return <TrendingDown className="text-danger" size={16} />
    }
    return <Minus className="text-white text-opacity-50" size={16} />
}

const CompactKpiRow: FC<{ kpi: KpiItem; color: string }> = ({ kpi, color }) => {
    const performance = calculatePerformance(kpi.value, kpi.target)
    const statusColor = getStatusColorFromText(kpi.status)

    return (
        <div className="rounded-3 bg-white bg-opacity-10 p-3 mb-2">
            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <small className="text-white text-opacity-90 lh-sm">{kpi.name}</small>
                <TrendIcon tranding={kpi.tranding} />
            </div>

            <div className="d-flex align-items-end justify-content-between gap-2 mb-2">
                <div>
                    <span className="fw-bold fs-4 text-white me-1">{kpi.value}</span>
                    <small className="text-white text-opacity-60">{kpi.unit}</small>
                </div>
                <small className="text-white text-opacity-60">
                    Target: {kpi.target} {kpi.unit}
                </small>
            </div>

            <div
                className="progress progress-sm mb-2"
                role="progressbar"
                aria-valuenow={performance}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ height: '6px', background: 'rgba(255,255,255,0.15)' }}
            >
                <div
                    className={`progress-bar bg-gradient-${color}`}
                    style={{ width: `${performance}%` }}
                />
            </div>

            <div className="d-flex justify-content-end">
                <span className={`badge rounded-pill bg-light-${statusColor} text-${statusColor} border border-${statusColor}`}>
                    {kpi.status}
                </span>
            </div>
        </div>
    )
}

const DepartmentSlide: FC<{ dept: DepartmentKpi }> = ({ dept }) => {
    const IconComponent = dept.icon

    return (
        <div className="w-100">
            <div className="d-flex align-items-start gap-3 mb-4 px-1">
                <div className={`bg-gradient-${dept.color} p-3 rounded-3 flex-shrink-0`}>
                    <IconComponent className="text-white" size={20} />
                </div>
                <div className="min-w-0">
                    <p className="text-white fw-semibold fs-5 mb-1 text-truncate">{dept.name}</p>
                    <small className="text-white text-opacity-65">{dept.description}</small>
                </div>
            </div>

            <div>
                {dept.kpis.map((kpi) => (
                    <CompactKpiRow key={kpi.name} kpi={kpi} color={dept.color} />
                ))}
            </div>

            <hr className="border-white border-opacity-25 my-3" />
            <small className="text-white text-opacity-55 d-block px-1">Source: {dept.source}</small>
        </div>
    )
}

const KpiDashboard: FC = () => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isPaused, setIsPaused] = useState(false)

    const total = DEPARTMENTS.length

    useEffect(() => {
        if (!total || isPaused) return

        const timer = window.setTimeout(() => {
            setActiveIndex((prev) => (prev + 1) % total)
        }, AUTO_SLIDE_MS)

        return () => window.clearTimeout(timer)
    }, [activeIndex, total, isPaused])

    if (!total) {
        return null
    }

    const activeDepartment = DEPARTMENTS[activeIndex]

    const goToPrevious = () => {
        setActiveIndex((prev) => (prev - 1 + total) % total)
    }

    const goToNext = () => {
        setActiveIndex((prev) => (prev + 1) % total)
    }

    return (
        <div
            className="widget-header-menu flex-column w-100 p-3 rounded-4"
            style={{
                maxWidth: '420px',
                minWidth: '420px',
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
        >
            <div className="menu-item rounded-4 w-100 p-0">
                <div className="menu-content px-2 py-2 rounded-4">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3 px-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-icon btn-active-color-primary text-white"
                            aria-label="Previous department"
                            onClick={goToPrevious}
                        >
                            <ChevronLeft size={20} />
                        </button>

                        <small className="text-white text-opacity-70">
                            {activeIndex + 1} / {total}
                        </small>

                        <button
                            type="button"
                            className="btn btn-sm btn-icon btn-active-color-primary text-white"
                            aria-label="Next department"
                            onClick={goToNext}
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <DepartmentSlide dept={activeDepartment} />

                    <div className="d-flex justify-content-center flex-wrap gap-1 mt-3 px-1">
                        {DEPARTMENTS.map((dept, index) => (
                            <button
                                key={dept.name}
                                type="button"
                                aria-label={`Show ${dept.name}`}
                                aria-current={
                                    index === activeIndex ? 'true' : undefined
                                }
                                className="border-0 p-0 rounded-circle"
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    background:
                                        index === activeIndex
                                            ? 'rgba(255,255,255,0.95)'
                                            : 'rgba(255,255,255,0.35)',
                                }}
                                onClick={() => setActiveIndex(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { KpiDashboard }